import { Injectable } from '@angular/core';
import { collection, collectionData, deleteDoc, doc, getFirestore, orderBy, query, where } from '@angular/fire/firestore';
import { ID_FIELD_OPTIONS, PRODUCTION_CUTTING_ITEMS_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService } from '@damofire/data-security';
import { clone } from 'lodash-es';
import { Observable } from 'rxjs';
import { CuttingItem } from './production-drawing.dto';

export type CuttingDimensionOverrides = Pick<CuttingItem, 'width' | 'height' | 'length' | 'angle'>;

const removeQuantityFromItem = (item: CuttingItem, quantity: number): number => {
  item.quantity -= quantity;
  if (item.quantity < 0) {
    const remaining = Math.abs(item.quantity);
    item.quantity = 0;
    return remaining;
  }

  return 0;
};

@Injectable()
export abstract class ProductionDrawingCuttingsService {
  abstract getCuttingsByFilename(filename: string): Observable<CuttingItem[]>;

  abstract updateGroupQuantity(cuttingItem: CuttingItem, variance: number, cuttingItems: CuttingItem[]): Promise<CuttingItem[]>;

  abstract updateItemsCategory(items: CuttingItem[], newGroup: string, cuttingItems: CuttingItem[], overrides?: Partial<CuttingDimensionOverrides>): Promise<CuttingItem[]>;

  abstract getCuttingsByJob(job: string): Observable<CuttingItem[]>;
}

@Injectable()
export class FirebaseProductionDrawingCuttingsService extends ProductionDrawingCuttingsService {
  constructor(private readonly dtoSigner: AuthDtoSignerService) {
    super();
  }

  getCuttingsByFilename(filename: string): Observable<CuttingItem[]> {
    return collectionData(query(collection(getFirestore(), PRODUCTION_CUTTING_ITEMS_COLLECTION), where('drawing', '==', filename)), ID_FIELD_OPTIONS) as Observable<CuttingItem[]>;
  }

  getCuttingsByJob(job: string): Observable<CuttingItem[]> {
    const whereClause = where('job', '==', job);
    const order = orderBy('model');
    return collectionData(query(collection(getFirestore(), PRODUCTION_CUTTING_ITEMS_COLLECTION), whereClause, order), ID_FIELD_OPTIONS) as Observable<CuttingItem[]>;
  }

  async updateGroupQuantity(cuttingItem: CuttingItem, variance: number, cuttingItems: CuttingItem[]): Promise<CuttingItem[]> {
    const updatedItems = cuttingItems.map((c: CuttingItem) => clone(c));
    const matching = updatedItems.find(this.cuttingItemMatcher(cuttingItem));

    if (!matching && variance) {
      this.addItem({ ...cuttingItem, quantity: variance }, updatedItems);
    } else if (matching) {
      if (variance > 0) {
        this.updateItem({ ...matching, quantity: matching.quantity + variance });
      } else {
        this.adjustItemsToSubtractVariance(matching, variance, updatedItems);
      }
    }

    return updatedItems;
  }

  async updateItemsCategory(items: CuttingItem[], newGroup: string, cuttingItems: CuttingItem[], overrides?: Partial<CuttingDimensionOverrides>): Promise<CuttingItem[]> {
    const updatedIds = new Set(items.map((i: CuttingItem) => i._id));
    const writes = items.map((item: CuttingItem) =>
      this.dtoSigner.update({ ...item, ...overrides, group: newGroup }, `${PRODUCTION_CUTTING_ITEMS_COLLECTION}/${item._id}`),
    );
    await Promise.all(writes);
    return cuttingItems.map((c: CuttingItem) => (updatedIds.has(c._id) ? { ...c, ...overrides, group: newGroup } : c));
  }

  private addItem(newItem: CuttingItem, group: CuttingItem[]): void {
    console.log(`Will add new cutting item doc.`);
    this.dtoSigner.add(newItem, PRODUCTION_CUTTING_ITEMS_COLLECTION).catch(console.error);
    group.push(newItem);
  }

  private updateItem(cuttingItem: CuttingItem): void {
    console.log(`Will update matching cutting item doc.`);
    this.dtoSigner.update(cuttingItem, `${PRODUCTION_CUTTING_ITEMS_COLLECTION}/${cuttingItem._id}`).catch(console.error);
  }

  private deleteItem(cuttingItem: CuttingItem, cuttingItems: CuttingItem[]): void {
    console.log(`Will delete matching cutting item doc.`);
    const matchingDoc = doc(getFirestore(), `${PRODUCTION_CUTTING_ITEMS_COLLECTION}/${cuttingItem._id}`);
    deleteDoc(matchingDoc).catch(console.error);

    cuttingItems.splice(
      cuttingItems.findIndex((ci: CuttingItem) => ci._id === cuttingItem._id),
      1,
    );
  }

  private adjustItemsToSubtractVariance(cuttingItem: CuttingItem, variance: number, cuttingItems: CuttingItem[]): void {
    console.log(`Will delete or update matching cutting items docs.`);
    cuttingItems.sort((a: CuttingItem, b: CuttingItem) => a.quantity - b.quantity);

    const allFromGroup: CuttingItem[] = cuttingItems.filter(this.cuttingItemMatcher(cuttingItem));
    allFromGroup.reduce((remaining: number, current: CuttingItem) => removeQuantityFromItem(current, remaining), Math.abs(variance));

    for (const i of allFromGroup) {
      if (i.quantity === 0) {
        this.deleteItem(i, cuttingItems);
      } else {
        this.updateItem(i);
      }
    }
  }

  private cuttingItemMatcher(cuttingItem: CuttingItem): (c: CuttingItem) => CuttingItem {
    let toHash: (c: CuttingItem) => string;

    switch (cuttingItem.group) {
      case 'CHANNEL':
        toHash = (c: CuttingItem) => `${c.perforated}${c.length}`;
        break;
      case 'BRACE':
        toHash = (c: CuttingItem) => `${c.length}${Math.round(c.angle)}`;
        break;
      case 'SEAT':
        toHash = (c: CuttingItem) => `${c.width}${c.height}`;
        break;
      case 'BACK_PLATE':
        toHash = (c: CuttingItem) => `${c.description}`;
        break;
      case 'HEAD':
        toHash = (c: CuttingItem) => `${c.front}${c.width}${c.height}${c.length}`;
        break;
      case 'BEIGNE':
        toHash = (c: CuttingItem) => c.description;
        break;
      default:
        toHash = () => undefined;
    }

    return (c: CuttingItem) => (toHash(c) === toHash(cuttingItem) ? c : undefined);
  }
}
